import { Injectable, inject } from '@angular/core';
import { Observable, throwError, forkJoin } from 'rxjs';
import { map, switchMap, catchError, finalize, tap } from 'rxjs/operators';
import { InvestigationFormRepository } from '../../../infrastructure/repositories/investigation-form.repository';
import { InvestigationFormState } from '../../states/investigation-form.state';
import { GetInvestigationFormLastVersionQuery } from './get-investigation-form-last-version.query';
import { InvestigationFormDetailEntity } from '../../../domain/entities/investigation-form-detail.entity';
import { InvestigationFormFieldEntity, InvestigationFormFieldType } from '../../../domain/entities/investigation-form-field.entity';
import { FormVersionDetailRepositoryResponseDto } from '../../../infrastructure/dtos/form-version-detail-repository-response.dto';
import { FormVersionSummaryResponseDto } from '../../../infrastructure/dtos/form-version-summary-response.dto';
import { FormVersionFieldResponseDto } from '../../../infrastructure/dtos/form-version-field-response.dto';

@Injectable({ providedIn: 'root' })
export class GetInvestigationFormLastVersionUseCase {
  private readonly _repository = inject(InvestigationFormRepository);
  private readonly _state = inject(InvestigationFormState);

  public execute(query: GetInvestigationFormLastVersionQuery): Observable<InvestigationFormDetailEntity> {
    this._state.setLoadingInvestigationForms(true);
    this._state.clearErrorInvestigationForms();

    return forkJoin({
      form: this._repository.getInvestigationFormById(query.formId),
      versions: this._repository.getInvestigationFormVersionsByFormId(query.formId),
    }).pipe(
      switchMap(({ form, versions }) => {
        if (!form) {
          throw new Error(`Investigation form ${query.formId} not found`);
        }

        const sortedVersions = [...versions.data].sort(
          (a: FormVersionSummaryResponseDto, b: FormVersionSummaryResponseDto) =>
            new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
        );

        const latestVersion = sortedVersions[0];

        if (!latestVersion) {
          throw new Error(`No versions found for investigation form ${query.formId}`);
        }

        return this._repository
          .getInvestigationFormVersionDetailByVersionId(latestVersion.versionId)
          .pipe(
            map((versionDetail: FormVersionDetailRepositoryResponseDto) => {
              const detailEntity = new InvestigationFormDetailEntity();

              detailEntity.id = form.id;
              detailEntity.name = form.name;
              detailEntity.displayName = form.name;
              detailEntity.description = form.description;
              detailEntity.investigationTypeId = form.investigationTypeId;
              detailEntity.investigationTypeName = form.investigationTypeName ?? '';
              detailEntity.createdAt = form.createdAt;
              detailEntity.updatedAt = form.updatedAt;
              detailEntity.createdBy = 'system';
              detailEntity.updatedBy = 'system';

              detailEntity.status = versionDetail.status;
              detailEntity.statusName = versionDetail.statusName;
              detailEntity.versionId = versionDetail.versionId;
              detailEntity.versionNumber = versionDetail.versionNumber;

              detailEntity.fields = versionDetail.fields.map((field: FormVersionFieldResponseDto) => {
                const fieldEntity = new InvestigationFormFieldEntity();
                fieldEntity.id = field.fieldId;
                fieldEntity.name = field.label.toLowerCase().replace(/\s+/g, '_');
                fieldEntity.label = field.label;
                fieldEntity.helperText = field.helperText ?? undefined;
                fieldEntity.placeholder = field.placeholder ?? undefined;
                fieldEntity.fieldType = field.fieldType as InvestigationFormFieldType;
                fieldEntity.isRequired = field.isRequired;
                fieldEntity.isVisibleToRequester = field.isVisibleToRequester;
                fieldEntity.isHiddenByDefault = field.isHiddenByDefault;
                fieldEntity.isSystem = field.isSystem;
                fieldEntity.sensitivityCategory = field.sensitivityCategory;
                fieldEntity.visibilityRule = field.visibilityRule
                  ? {
                      sourceFieldId: field.visibilityRule.sourceFieldId,
                      operator: field.visibilityRule.operator,
                      value: field.visibilityRule.value || '',
                      show: field.visibilityRule.show,
                    }
                  : null;
                fieldEntity.sortOrder = field.sortOrder;
                fieldEntity.options = undefined;
                fieldEntity.defaultValue = undefined;
                fieldEntity.validationRules = undefined;
                fieldEntity.constraints = field.constraints || [];
                return fieldEntity;
              });

              return detailEntity;
            })
          );
      }),
      tap((detailEntity) => this._state.setInvestigationFormSelectedToEdit(detailEntity)),
      catchError((error) => {
        const errorMessage =
          error?.error?.message ||
          error?.message ||
          `Failed to load investigation form version for form ${query.formId}`;
        this._state.setErrorInvestigationForms(errorMessage);
        return throwError(() => error);
      }),
      finalize(() => this._state.setLoadingInvestigationForms(false))
    );
  }
}
